import * as React from 'react';
import Box from '@mui/material/Box';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';
import { useQuery } from 'react-query';
import { bvdApiClient, IApiHosts } from 'utils/bvdApiClient';
import { CircularProgress, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useRouter } from 'next/router';

interface RatingValueProps {
  name: string;
  value: number;
}

const CONDITION_UUID_VERY_GOOD = '0125fa46-d343-499f-91f0-bed3f336eb22';

const PriceValue = ({ name, value }: RatingValueProps) => {
  const { locale } = useRouter();
  const { isLoading: isLoadingProduct, data: productData } = useQuery(
    ['product', value, CONDITION_UUID_VERY_GOOD, locale],
    () =>
      bvdApiClient(locale).userSells.products.getByExternalIdWithConditionLevel(
        value,
        CONDITION_UUID_VERY_GOOD
      ),
    {
      refetchOnWindowFocus: false,
    }
  );

  const MINUTES_OF_HOUR = 60;
  const { isLoading: isLoadingOffers, data: offers } = useQuery(
    ['offer', CONDITION_UUID_VERY_GOOD, productData?.payload?.uuid, locale],
    () =>
      bvdApiClient(locale).userSells.offers.getOffersForProduct(
        productData.payload.uuid,
        null,
        6 * MINUTES_OF_HOUR
      ),
    {
      enabled: !!productData,
      refetchOnWindowFocus: false,
    }
  );
  const bestOffer = offers?.payload.sort(
    (a: any, b: any) => b.price - a.price
  )[0];

  const offerDate = new Date(bestOffer?.timestamp);
  return isLoadingOffers ? (
    <Box sx={{ display: 'flex' }}>
      <CircularProgress size={20} />
    </Box>
  ) : (
    <Box>
      <Typography variant="body1">{bestOffer?.price.toFixed(2)} €</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <AccessTimeIcon sx={{ fontSize: 12 }} />
        <Typography
          color="textSecondary"
          fontSize={12}
          sx={{ pl: 0.5 }}
          variant="body2"
        >
          {offerDate.toLocaleDateString(process.env.NEXT_PUBLIC_LOCAL)}{' '}
          {offerDate.toLocaleTimeString(process.env.NEXT_PUBLIC_LOCAL)}
        </Typography>
      </Box>
    </Box>
  );
};

const MemoizedPriceValue = React.memo(PriceValue);

export function renderPrice(
  params: GridRenderCellParams<number, { id: GridRowId }, any>
) {
  if (params.rowNode.isAutoGenerated || params.value == null) {
    return '';
  }
  return (
    <MemoizedPriceValue value={params.value} name={params.row.id.toString()} />
  );
}
